import React, { useState } from "react";
import { Box, Checkbox, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { ShoppingBagOutlined ,PictureAsPdfOutlined} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PodOrder } from "../../../../../models/PodOrder/PodOrder";
import { PodOrderProgressStage } from "../../../../../models/PodOrder/PodOrderProgressStage";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import {
  PodOrderPrintingTypeBadge,
  PodOrderProgressStageBadge,
  PodOrderPreviouslyAllocatedBadge,
} from "./podOrderBadges";

interface PodOrderListCardProps {
  order: PodOrder;
  selected: boolean;
  selectionEnabled: boolean;
  onToggleRow: (order: PodOrder) => void;
  onShipmentIdClick?: (shipmentId: number) => void;
  showAvailableQty?: boolean;
}

const displayQty = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "" || value === 0 || value === "0") {
    return "-";
  }
  return String(value);
};

const MetaField: React.FC<{
  label: string;
  children: React.ReactNode;
  align?: "left" | "right";
  compact?: boolean;
  fill?: boolean;
}> = ({ label, children, align = "left", compact = false, fill = false }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);

  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <CustomTypography
        variant="heading9"
        color={colors.subtitle}
        title={label}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </CustomTypography>
      <Box
        sx={{
          mt: 0.5,
          minWidth: 0,
          ...(compact
            ? {
                minHeight: 32,
                display: "flex",
                alignItems: "center",
                ...(fill
                  ? { "& > *": { flex: 1, minWidth: 0, width: "100%" } }
                  : {}),
              }
            : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const ValueText: React.FC<{
  children: React.ReactNode;
  color?: string;
  onClick?: () => void;
}> = ({ children, color, onClick }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);

  return (
    <CustomTypography
      variant="body2"
      fontWeight={600}
      color={color ?? colors.title}
      onClick={onClick}
      title={
        typeof children === "string" || typeof children === "number"
          ? String(children)
          : undefined
      }
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {children}
    </CustomTypography>
  );
};

const filePath = (url: string) => url.split("?")[0].split("#")[0].toLowerCase();

const isPdfUrl = (url: string) => filePath(url).endsWith(".pdf");

const UrlThumb: React.FC<{ url?: string | null; alt: string }> = ({ url, alt }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const trimmed = url?.trim() || "";
  const [asPdf, setAsPdf] = useState(() => (trimmed ? isPdfUrl(trimmed) : false));

  if (!trimmed) {
    return (
      <CustomTypography variant="body2" fontWeight={600} color={colors.title}>
        —
      </CustomTypography>
    );
  }

  const openFile = (event: React.MouseEvent) => {
    try {
      event.stopPropagation();
      window.open(trimmed, "_blank", "noopener,noreferrer");
    } catch {
      // Ignore popup blockers; user can retry.
    }
  };

  return (
    <Box
      onClick={openFile}
      role="link"
      aria-label={asPdf ? `Open ${alt} PDF` : alt}
      sx={{
        width: "100%",
        maxWidth: 113,
        height: 76,
        borderRadius: "8px",
        overflow: "hidden",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.headerBg,
        cursor: "pointer",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 0.5,
        "&:hover": {
          borderColor: theme.palette.customColors.purpleMain,
        },
      }}
    >
      {asPdf ? (
        <>
          <PictureAsPdfOutlined
            sx={{ fontSize: 28, color: theme.palette.customColors.errorRed }}
          />
          <CustomTypography variant="caption" fontWeight={600} color={colors.subtitle}>
            Open PDF
          </CustomTypography>
        </>
      ) : (
        <Box
          component="img"
          src={trimmed}
          alt={alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setAsPdf(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
    </Box>
  );
};

const ProductBlock: React.FC<{ order: PodOrder }> = ({ order }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          bgcolor: colors.headerBg,
          border: `1px solid ${colors.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <ShoppingBagOutlined sx={{ fontSize: 18, color: colors.subtitle }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <CustomTypography
          variant="body2"
          fontWeight={700}
          color={colors.title}
          title={order.product_name?.trim() || "—"}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {order.product_name?.trim() || "—"}
        </CustomTypography>
        <Box
          sx={{
            mt: 0.25,
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            "& > *": { maxWidth: "100%", minWidth: 0 },
          }}
        >
          <SkuCopyRow sku={order.sku} emptyFallback="—" variant="body3" />
        </Box>
      </Box>
    </Box>
  );
};

const PodOrderListCard: React.FC<PodOrderListCardProps> = ({
  order,
  selected,
  selectionEnabled,
  onToggleRow,
  onShipmentIdClick,
  showAvailableQty = false,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const colors = getModernPageColors(theme);
  const navigate = useNavigate();
  const linkColor = theme.palette.customColors.purpleMain;
  // Box/Rack = where raw stock was picked from; only meaningful until it leaves for printing.
  const showBoxAndRack =
    order.status === PodOrderProgressStage.INVENTORY_ALLOCATED ||
    order.status === PodOrderProgressStage.OUT_FOR_PRINTING;
  const boxIdValue = order.box_id?.trim() || "—";
  const rackIdValue = order.rack_id?.trim() || "—";
  const previouslyAllocatedBadge =
    order.is_previously_allocated &&
    order.status === PodOrderProgressStage.UNASSIGNED ? (
      <PodOrderPreviouslyAllocatedBadge
        unassignedAt={order.unassigned_at}
        unassignedByName={order.unassigned_by_name}
      />
    ) : null;

  const openOrder = () => {
    try {
      navigate(`/orders/add-orders?orderId=${order.order_id}`);
    } catch {
      // Router navigation failures stay on the list.
    }
  };

  const openShipment = (shipmentId: number) => {
    try {
      onShipmentIdClick?.(shipmentId);
    } catch {
      // Shipment drawer open is owned by the parent.
    }
  };

  const checkbox = selectionEnabled ? (
    <Checkbox
      color="primary"
      size="small"
      checked={selected}
      onClick={(event) => {
        event.stopPropagation();
        onToggleRow(order);
      }}
      sx={{ p: 0.5 }}
    />
  ) : null;

  const orderId = (
    <Box
      onClick={openOrder}
      sx={{
        width: "fit-content",
        cursor: "pointer",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <CustomTypography variant="heading6" fontWeight={700} color={linkColor}>
        #{order.order_id}
      </CustomTypography>
    </Box>
  );

  const badgeColumnSx = {
    width: 108,
    flex: "0 0 auto",
    overflow: "hidden",
    "& span": {
      display: "inline-block",
      width: "auto",
      maxWidth: "100%",
      px: 0.75,
      py: 0.15,
      fontSize: "0.68rem",
      lineHeight: 1.4,
    },
    [theme.breakpoints.down(1280)]: { width: 84 },
  };

  const shipmentItems =
    order.shipments && order.shipments.length > 0
      ? order.shipments
      : order.shipment_id
        ? [{ shipment_id: order.shipment_id, cancelled: false }]
        : [];

  const shipmentValue =
    shipmentItems.length > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {shipmentItems.map((shipment) => (
          <ValueText
            key={shipment.shipment_id}
            color={linkColor}
            onClick={() => openShipment(shipment.shipment_id)}
          >
            <span
              style={{
                textDecoration: shipment.cancelled ? "line-through" : undefined,
              }}
            >
              {shipment.shipment_id}
            </span>
          </ValueText>
        ))}
      </Box>
    ) : (
      <ValueText>—</ValueText>
    );

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: `2px solid ${selected ? theme.palette.customColors.softBorder : colors.border}`,
        bgcolor: selected ? colors.surfaceSoft : colors.surface,
        boxShadow: colors.shadowSoft,
        overflow: "hidden",
        flexShrink: 0,
        minHeight: "min-content",
        width: "100%",
      }}
    >
      {isDesktop ? (
      <Box sx={{ minWidth: 0, overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              px: 1.5,
              py: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                alignSelf: "center",
                flexShrink: 0,
                gap: 0.1,
              }}
            >
              {checkbox}
              <Box
                sx={{
                  width: 70,
                  flexShrink: 0,
                  [theme.breakpoints.up(1280)]: { width: 80 },
                }}
              >
                {orderId}
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "grid",
                // Cart, SIR, Warehouse, optional Avl Qty / Box+Rack, dates, shipment.
                gridTemplateColumns: showAvailableQty
                  ? "12fr 18fr 14fr 18fr 11fr 24fr 24fr 16fr"
                  : showBoxAndRack
                    ? "10fr 16fr 10fr 14fr 12fr 12fr 18fr 18fr 14fr"
                    : "12fr 18fr 14fr 18fr 24fr 24fr 16fr",
                columnGap: 1.25,
                alignItems: "end",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="Cart ID">
                  <ValueText>{order.cart_id?.trim() || "—"}</ValueText>
                </MetaField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="Store Name">
                  <ValueText>{order.store_name?.trim() || "—"}</ValueText>
                </MetaField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="SIR ID">
                  <ValueText>{order.stock_issue_request_id ?? "—"}</ValueText>
                </MetaField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact fill label="Warehouse">
                  <ValueText>
                    {order.warehouse_id?.trim() || "—"}
                  </ValueText>
                </MetaField>
              </Box>
              {showAvailableQty ? (
                <Box sx={{ minWidth: 0 }} title="Available quantity in this warehouse">
                  <MetaField compact label="Avl Qty">
                    <ValueText>
                      {order.available_qty == null ? "—" : order.available_qty}
                    </ValueText>
                  </MetaField>
                </Box>
              ) : null}
              {showBoxAndRack ? (
                <Box sx={{ minWidth: 0 }} title="Swageazy inventory box">
                  <MetaField compact label="Box ID">
                    <ValueText>{boxIdValue}</ValueText>
                  </MetaField>
                </Box>
              ) : null}
              {showBoxAndRack ? (
                <Box sx={{ minWidth: 0 }} title="Swageazy inventory rack">
                  <MetaField compact label="Rack ID">
                    <ValueText>{rackIdValue}</ValueText>
                  </MetaField>
                </Box>
              ) : null}
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="Order Date">
                  <ValueText>{formatDateWithoutTime(order.order_date)}</ValueText>
                </MetaField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="Expected Dispatch">
                  <ValueText>{formatDateWithoutTime(order.dispatch_date)}</ValueText>
                </MetaField>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <MetaField compact label="Shipment ID">
                  {shipmentValue}
                </MetaField>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexShrink: 0 }}>
              <Box sx={badgeColumnSx}>
                <MetaField compact label="Printing type">
                  <PodOrderPrintingTypeBadge name={order.printing_type} variant="pill" />
                </MetaField>
              </Box>
              <Box sx={badgeColumnSx}>
                <MetaField compact label="Stage">
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <PodOrderProgressStageBadge status={order.status} />
                    {previouslyAllocatedBadge}
                  </Box>
                </MetaField>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mx: 1.5, borderTop: `1px solid ${colors.border}` }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "minmax(180px, 1.5fr) minmax(120px, 1fr) repeat(4, minmax(72px, 0.7fr)) 96px 96px",
              columnGap: 1.25,
              alignItems: "flex-start",
              px: 1.5,
              py: 1.5,
              minWidth: 0,
            }}
          >
 <Box sx={{ minWidth: 0, overflow: "hidden", alignSelf: "center" }}>
              <ProductBlock order={order} />
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              <MetaField label="Customer">
                <ValueText>{order.customer_name?.trim() || "—"}</ValueText>
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              <MetaField label="Order Qty">
                <ValueText>{order.ordered_qty}</ValueText>
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              <MetaField label="Outward Qty">
                <ValueText>{displayQty(order.total_quantity_sent)}</ValueText>
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              <MetaField label="Inward Qty">
                <ValueText>{displayQty(order.total_print_inwarded_qty)}</ValueText>
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              <MetaField label="Rejected Qty">
                <ValueText>{displayQty(order.total_rejected_quantity)}</ValueText>
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <MetaField label="Mockup URL">
                <UrlThumb url={order.mockup_url} alt="Mockup" />
              </MetaField>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <MetaField label="Print URL">
                <UrlThumb url={order.printing_url} alt="Print" />
              </MetaField>
            </Box>
          </Box>
      </Box>
      ) : (
      <Box sx={{ p: 1.75 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {checkbox}
            {orderId}
          </Box>
          <MetaField label="Stage" align="right">
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <PodOrderProgressStageBadge status={order.status} />
              {previouslyAllocatedBadge}
            </Box>
          </MetaField>
        </Box>

        <Grid2 container spacing={1.5} sx={{ mt: 2 }}>
          <Grid2 size={4}>
            <MetaField label="Printing type">
              <PodOrderPrintingTypeBadge name={order.printing_type} variant="pill" />
            </MetaField>
          </Grid2>
          <Grid2 size={3}>
            <MetaField label="Cart ID">
              <ValueText>{order.cart_id?.trim() || "—"}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={5}>
            <MetaField label="Store Name">
              <ValueText>{order.store_name?.trim() || "—"}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={4}>
            <MetaField label="SIR ID">
              <ValueText>{order.stock_issue_request_id ?? "—"}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={12}>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5 }}>
              <Box sx={{ width: showAvailableQty ? "50%" : "100%", minWidth: 0 }}>
                <MetaField label="Warehouse">
                  <ValueText>
                    {order.warehouse_id?.trim() || "—"}
                  </ValueText>
                </MetaField>
              </Box>
              {showAvailableQty ? (
                <Box sx={{ flexShrink: 0 }}>
                  <MetaField label="Avl Qty">
                    <ValueText>
                      {order.available_qty == null ? "—" : order.available_qty}
                    </ValueText>
                  </MetaField>
                </Box>
              ) : null}
            </Box>
          </Grid2>
          {showBoxAndRack ? (
            <>
              <Grid2 size={6}>
                <MetaField label="Box ID">
                  <ValueText>{boxIdValue}</ValueText>
                </MetaField>
              </Grid2>
              <Grid2 size={6}>
                <MetaField label="Rack ID">
                  <ValueText>{rackIdValue}</ValueText>
                </MetaField>
              </Grid2>
            </>
          ) : null}
          <Grid2 size={6}>
            <MetaField label="Expected Dispatch">
              <ValueText>{formatDateWithoutTime(order.dispatch_date)}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Order Date">
              <ValueText>{formatDateWithoutTime(order.order_date)}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Shipment ID">{shipmentValue}</MetaField>
          </Grid2>
        </Grid2>

        <Box sx={{ my: 1.75, borderTop: `1px solid ${colors.border}` }} />

        <ProductBlock order={order} />

        <Box sx={{ mt: 1.75 }}>
          <MetaField label="Customer">
            <ValueText>{order.customer_name?.trim() || "—"}</ValueText>
          </MetaField>
        </Box>

        <Grid2 container spacing={1.5} sx={{ mt: 1.75 }}>
          <Grid2 size={6}>
            <MetaField label="Order Qty">
              <ValueText>{order.ordered_qty}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Outward Qty">
              <ValueText>{displayQty(order.total_quantity_sent)}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Inward Qty">
              <ValueText>{displayQty(order.total_print_inwarded_qty)}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Rejected Qty">
              <ValueText>{displayQty(order.total_rejected_quantity)}</ValueText>
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Mockup URL">
              <UrlThumb url={order.mockup_url} alt="Mockup" />
            </MetaField>
          </Grid2>
          <Grid2 size={6}>
            <MetaField label="Print URL">
              <UrlThumb url={order.printing_url} alt="Print" />
            </MetaField>
          </Grid2>
        </Grid2>
      </Box>
      )}
    </Box>
  );
};

export default PodOrderListCard;
